import { useState } from "react";
import { ImageUpload } from "@/components/ImageUpload";
import { fileUrl } from "@/lib/api";
import { X, Plus } from "lucide-react";

export const inputCls = "w-full rounded border border-kys-blush-border bg-white px-3 py-2 text-sm focus:border-kys-accent focus:outline-none";

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-kys-charcoal">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-kys-muted">{hint}</span>}
    </label>
  );
}

export function Text({ label, value, onChange, placeholder, type = "text", hint, testid }) {
  return (
    <Field label={label} hint={hint}>
      <input data-testid={testid} type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        className={inputCls} />
    </Field>
  );
}

export function Area({ label, value, onChange, rows = 3, hint, testid }) {
  return (
    <Field label={label} hint={hint}>
      <textarea data-testid={testid} rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />
    </Field>
  );
}

export function Select({ label, value, onChange, options, testid }) {
  return (
    <Field label={label}>
      <select data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls}>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Field>
  );
}

export function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded border border-kys-blush-border bg-white px-3 py-2.5">
      <span className="text-sm text-kys-charcoal">{label}</span>
      <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 rounded-full transition ${checked ? "bg-kys-accent" : "bg-kys-blush-border"}`}>
        <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition ${checked ? "left-5" : "left-0.5"}`} />
      </button>
    </label>
  );
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/50 p-4 py-10" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} rounded-lg bg-white shadow-2xl`}>
        <div className="flex items-center justify-between border-b border-kys-blush-border px-5 py-3.5">
          <h3 className="font-serif text-xl text-kys-ink">{title}</h3>
          <button onClick={onClose} data-testid="modal-close"><X className="h-5 w-5 text-kys-muted" /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    "New Request": "bg-kys-accent/15 text-kys-accent-dark", "Reviewing": "bg-blue-100 text-blue-700",
    "Need More Information": "bg-amber-100 text-amber-700", "Quoted": "bg-purple-100 text-purple-700",
    "Accepted": "bg-green-100 text-green-700", "Approved": "bg-kys-accent text-white", "Declined": "bg-red-100 text-red-700", "Completed": "bg-kys-charcoal/10 text-kys-charcoal",
    "Pending Payment": "bg-amber-100 text-amber-700", "Paid": "bg-green-100 text-green-700", "Processing": "bg-blue-100 text-blue-700",
    "Shipped": "bg-purple-100 text-purple-700", "Delivered": "bg-green-100 text-green-700", "Cancelled": "bg-red-100 text-red-700", "Refunded": "bg-gray-200 text-gray-700",
  };
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${map[status] || "bg-gray-100 text-gray-600"}`}>{status}</span>;
}

export function SingleImageEditor({ value, onChange, label = "Image" }) {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-kys-charcoal">{label}</span>
      {value?.url ? (
        <div className="relative inline-block">
          <img src={fileUrl(value)} alt="" className="h-28 w-28 rounded border border-kys-blush-border object-cover" />
          <button type="button" onClick={() => onChange(null)} className="absolute -right-2 -top-2 rounded-full bg-kys-ink p-1 text-white"><X className="h-3 w-3" /></button>
        </div>
      ) : <ImageUpload onUploaded={onChange} label="Upload" />}
    </div>
  );
}

export function ImagesEditor({ value = [], onChange }) {
  const list = value || [];
  return (
    <div>
      <span className="mb-1 block text-xs font-medium text-kys-charcoal">Photos</span>
      <div className="flex flex-wrap gap-3">
        {list.map((im, i) => (
          <div key={i} className="w-28">
            <div className="relative">
              <img src={fileUrl(im)} alt={im.alt || ""} className="h-28 w-28 rounded border border-kys-blush-border object-cover" />
              <button type="button" onClick={() => onChange(list.filter((_, idx) => idx !== i))} className="absolute -right-2 -top-2 rounded-full bg-kys-ink p-1 text-white"><X className="h-3 w-3" /></button>
            </div>
            <input placeholder="Alt text" value={im.alt || ""} onChange={(e) => onChange(list.map((x, idx) => idx === i ? { ...x, alt: e.target.value } : x))}
              className="mt-1 w-28 rounded border border-kys-blush-border px-1.5 py-1 text-[11px]" />
          </div>
        ))}
        <div className="flex h-28 w-28 items-center justify-center rounded border border-dashed border-kys-blush-border">
          <ImageUpload testid="add-photo" onUploaded={(img) => onChange([...list, img])} label={<Plus className="h-4 w-4" />} />
        </div>
      </div>
    </div>
  );
}
